import { Injectable, OnDestroy } from '@angular/core';
import { computeRouteGraphPaths, IRouteGraphRequest, IRouteGraphResult } from './route-graph.compute';
import { IRouteTimes } from 'src/app/models';

@Injectable({ providedIn: 'root' })
export class RouteGraphService implements OnDestroy {

  private worker: Worker | null = null;
  private workerFailed = false;
  private requestCounter = 0;
  private pendingResolvers = new Map<number, (result: IRouteGraphResult) => void>();

  // Heavy path exploration runs off the main thread so big graphs never freeze the UI;
  // callers should treat the promise as advisory and discard superseded results
  computePaths(graph: IRouteGraphRequest): Promise<IRouteGraphResult> {
    const worker = this.getWorker();
    if (!worker) return Promise.resolve(computeRouteGraphPaths(graph));
    const requestId = ++this.requestCounter;
    return new Promise((resolve) => {
      this.pendingResolvers.set(requestId, resolve);
      worker.postMessage({ requestId, graph });
    });
  }

  async computeShortestPathSeconds(graph: IRouteGraphRequest): Promise<number | null> {
    const result = await this.computePaths({ ...graph, shortestPathOnly: true });
    return result.shortestPathSeconds;
  }

  async computeRouteTimes(graph: IRouteGraphRequest): Promise<IRouteTimes | null> {
    const result = await this.computePaths(graph);
    return routeTimesOf(result);
  }

  private getWorker(): Worker | null {
    if (this.workerFailed) return null;
    if (this.worker) return this.worker;
    try {
      this.worker = new Worker(new URL('./route-graph.worker', import.meta.url), { type: 'module' });
      this.worker.onmessage = ({ data }: MessageEvent<{ requestId: number; result: IRouteGraphResult }>) => {
        const resolve = this.pendingResolvers.get(data.requestId);
        this.pendingResolvers.delete(data.requestId);
        resolve?.(data.result);
      };
      this.worker.onerror = () => {
        this.workerFailed = true;
        this.worker?.terminate();
        this.worker = null;
        const emptyResult: IRouteGraphResult = { shortestPathStopIds: null, shortestPathSeconds: null, coverageStopIds: null, coverageSeconds: null, coverageApproximate: false };
        this.pendingResolvers.forEach((resolve) => resolve(emptyResult));
        this.pendingResolvers.clear();
      };
      return this.worker;
    } catch {
      this.workerFailed = true;
      return null;
    }
  }

  ngOnDestroy(): void {
    this.worker?.terminate();
    this.worker = null;
    this.pendingResolvers.clear();
  }
}

export function routeTimesOf(result: IRouteGraphResult): IRouteTimes | null {
  if (result.shortestPathSeconds == null || result.coverageSeconds == null) return null;
  return {
    minPathTime: result.shortestPathSeconds,
    maxPathTime: result.coverageSeconds,
    maxVariability: result.coverageSeconds - result.shortestPathSeconds,
  };
}
